'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { getAuthToken, getRefreshToken } from '@/utils/auth'
import { refreshTokens } from '@/utils/refreshTokens'

import type { NotificationItem } from '../api/notificationsApi'
import { invalidateFeed, takeForeignReads } from '../lib/feedCache'
import {
  firstReconnectDelay,
  nextReconnectDelay,
  RECONNECT_MAX_MS,
  RECONNECT_MIN_MS
} from '../lib/reconnectDelay'
import { tokenExpiresWithin, userIdFromToken } from '../lib/sessionUser'
import { createSseParser } from '../lib/sseParser'
import { notificationsQueryKeys } from '../notifications.constants'
import { useNotificationsUserId } from './useNotificationsUserId'

const STREAM_URL = '/api/v2/notifications/sse'

/**
 * How long a stream must stay up to count as healthy.
 *
 * Accepted headers are not health: a replica that accepts the connection and
 * drops it at once — crash-looping, or evicting the connection it just took —
 * would otherwise have every client back on the one-second delay, forever.
 * The server sends a keepalive every 15s, so this is two of them.
 */
const STABLE_MS = 30_000

/**
 * 401s in a row after which the stream gives up. Each one spends a refresh
 * rotation, and a token the gateway keeps refusing will not start working.
 */
const MAX_UNAUTHORIZED = 3

/**
 * How long a hidden tab keeps its stream. The gateway holds three streams per
 * account and evicts the oldest, so background tabs would push the visible one
 * out; the grace keeps a quick tab switch from reconnecting.
 */
export const HIDDEN_GRACE_MS = 15_000

/**
 * The gateway ends the stream when the access token expires. A token this
 * close to expiry is renewed before connecting instead of earning a 401.
 */
const TOKEN_MARGIN_MS = 30_000

/** Events closer together than this are one refetch. */
const FEED_BATCH_MS = 250

/**
 * Data fetched this recently is not refetched when the stream first opens:
 * the bell has just loaded it on mount.
 */
const FRESH_MS = 10_000

/**
 * A resync reaches every client of every gateway replica at once (Redis came
 * back); each waits a random delay of up to this before refetching.
 */
export const RESYNC_JITTER_MS = 2000

/**
 * How a connection attempt ended. `stop`: the session is over or belongs to
 * another account — the effect reruns for the next one. The others reconnect;
 * `unauthorized` first renews the token.
 */
type Outcome = 'ended' | 'failed' | 'unauthorized' | 'stop'

/** Renews the access token; undefined when it could not (for now). */
const renewToken = async () => {
  if (!getRefreshToken()) {
    return undefined
  }

  try {
    return await refreshTokens()
  } catch {
    return undefined
  }
}

/**
 * Live feed over SSE.
 *
 * EventSource cannot carry an Authorization header, so the stream is read with
 * fetch: the gateway authenticates the request like any other. It is reopened
 * with a jittered, capped backoff — a restarted gateway replica would
 * otherwise be met by all of its former clients at once — except after a 401,
 * which is retried at once with a renewed token.
 *
 * The stream belongs to one account: it is torn down and reopened when the
 * account changes, and only runs while the tab is visible. It stops for good
 * only when the session is over; a failed token renewal backs off, and three
 * refused tokens in a row wait for the fan to come back to the tab.
 */
export function useNotificationsStream(
  enabled = true,
  onNotification?: (notification: NotificationItem) => void
) {
  const queryClient = useQueryClient()
  const userId = useNotificationsUserId()
  const handlerRef = useRef(onNotification)

  // Declared before the stream's effect, so it runs first on every commit.
  useEffect(() => {
    handlerRef.current = onNotification
  })

  useEffect(() => {
    if (!enabled || !userId || typeof window === 'undefined') {
      return
    }

    let disposed = false
    let running = false
    let live = false
    let everOpened = false
    let backoff = RECONNECT_MIN_MS
    let unauthorized = 0
    let openedAt = 0
    let renewed: string | undefined
    let controller: AbortController | undefined
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let hideTimer: ReturnType<typeof setTimeout> | undefined
    let feedTimer: ReturnType<typeof setTimeout> | undefined
    let wake: (() => void) | undefined

    const isHidden = () => document.visibilityState === 'hidden'

    const refreshFeed = (delay = FEED_BATCH_MS) => {
      feedTimer ??= setTimeout(() => {
        feedTimer = undefined
        invalidateFeed(queryClient, userId)
      }, delay)
    }

    const isFresh = () =>
      [
        notificationsQueryKeys.unreadCount(userId),
        notificationsQueryKeys.list(userId)
      ].every(queryKey => {
        const state = queryClient.getQueryState(queryKey)
        return (
          !state ||
          state.fetchStatus === 'fetching' ||
          (state.status === 'success' &&
            Date.now() - state.dataUpdatedAt < FRESH_MS)
        )
      })

    const handlePayload = (raw: string) => {
      let payload: unknown
      try {
        payload = JSON.parse(raw)
      } catch {
        return
      }
      if (!payload || typeof payload !== 'object') {
        return
      }

      const { event } = payload as { event?: unknown }

      if (event === 'read') {
        const { ids } = payload as { ids?: unknown }
        const read = Array.isArray(ids)
          ? ids.filter((id): id is string => typeof id === 'string')
          : []
        // This tab's own read is already applied to its cache.
        if (read.length > 0 && takeForeignReads(read).length === 0) {
          return
        }
        refreshFeed()
        return
      }

      // The gateway lost messages (a Redis restart): the feed has to be read
      // again from the database.
      if (event === 'resync') {
        refreshFeed(Math.random() * RESYNC_JITTER_MS)
        return
      }

      if (event !== undefined) {
        return
      }

      const notification = payload as NotificationItem
      if (typeof notification.id !== 'string') {
        return
      }

      refreshFeed()
      handlerRef.current?.(notification)
    }

    const connect = async (): Promise<Outcome> => {
      // The access token outlives neither the session nor the stream: it
      // expires in minutes against a refresh token measured in days.
      let token = renewed ?? getAuthToken()
      renewed = undefined
      if (!token || tokenExpiresWithin(token, TOKEN_MARGIN_MS)) {
        token = (await renewToken()) ?? token
      }
      if (disposed) {
        return 'stop'
      }
      if (!token) {
        return getRefreshToken() ? 'failed' : 'stop'
      }
      // The cookies are shared by every tab; another one may have switched
      // the account. That account gets its own stream once this tab notices.
      if (userIdFromToken(token) !== userId) {
        return 'stop'
      }

      controller = new AbortController()

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL ?? ''}${STREAM_URL}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        }
      )
      if (disposed) {
        controller.abort()
        return 'stop'
      }

      if (response.status === 401) {
        return 'unauthorized'
      }
      if (!response.ok || !response.body) {
        return 'failed'
      }

      unauthorized = 0
      openedAt = Date.now()
      live = true
      // Whatever arrived while the stream was down is only in the database.
      // On the first open the bell has usually just fetched it.
      if (everOpened || !isFresh()) {
        refreshFeed()
      }
      everOpened = true

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      const parser = createSseParser(event => handlePayload(event.data))

      for (;;) {
        const { done, value } = await reader.read()
        if (disposed) {
          return 'stop'
        }
        if (done) {
          return 'ended'
        }
        parser.push(decoder.decode(value, { stream: true }))
      }
    }

    const sleep = (ms: number) =>
      new Promise<void>(resolve => {
        wake = resolve
        retryTimer = setTimeout(resolve, ms)
      })

    const backOff = async () => {
      const lived = openedAt === 0 ? 0 : Date.now() - openedAt
      let delay: number
      if (lived >= STABLE_MS) {
        backoff = RECONNECT_MIN_MS
        delay = firstReconnectDelay()
      } else {
        delay = nextReconnectDelay(backoff)
        backoff = Math.min(backoff * 2, RECONNECT_MAX_MS)
      }
      await sleep(delay)
    }

    const loop = async () => {
      running = true
      try {
        while (!disposed) {
          if (isHidden()) {
            return
          }

          openedAt = 0
          let outcome: Outcome
          try {
            outcome = await connect()
          } catch {
            outcome = 'failed'
          }
          controller = undefined
          live = false

          if (disposed || outcome === 'stop') {
            return
          }

          if (outcome === 'unauthorized') {
            unauthorized += 1
            if (unauthorized >= MAX_UNAUTHORIZED) {
              return
            }
            // An expired token is not an outage: retry at once with a new
            // one. A renewal that fails for now is one, so it backs off.
            renewed = await renewToken()
            if (disposed) {
              return
            }
            if (!renewed) {
              if (!getRefreshToken()) {
                return
              }
              await backOff()
            }
            continue
          }

          if (isHidden()) {
            continue
          }

          await backOff()
        }
      } finally {
        running = false
      }
    }

    const resume = () => {
      if (!live) {
        // Nothing tells the badge it is out of date while the stream is down.
        void queryClient.invalidateQueries({
          queryKey: notificationsQueryKeys.unreadCount(userId)
        })
      }

      if (running || disposed || !getRefreshToken()) {
        return
      }
      unauthorized = 0
      backoff = RECONNECT_MIN_MS
      void loop()
    }

    const onVisibilityChange = () => {
      clearTimeout(hideTimer)

      if (isHidden()) {
        hideTimer = setTimeout(() => controller?.abort(), HIDDEN_GRACE_MS)
        return
      }

      resume()
    }

    const onFocus = () => {
      if (!isHidden()) {
        resume()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)
    void loop()

    return () => {
      disposed = true
      clearTimeout(retryTimer)
      clearTimeout(hideTimer)
      clearTimeout(feedTimer)
      wake?.()
      controller?.abort()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
    }
  }, [enabled, userId, queryClient])
}
